import { useEffect, useContext } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';

import { getCombinedBannerAndHorizontalNavHeight } from 'src/selectors/global-selectors';
import { BucketState } from 'src/types/state';

import { useConsentBanner } from '../navigation-provider';

import { SidebarContext } from './context';

// TODO add polyfill for position: sticky
// https://softwareteams.atlassian.net/browse/BBCDEV-10779
const Container = styled.div<{
  topOffset: number;
  nav4Offset?: number;
}>(props => ({
  display: 'flex',
  /* Changing this to 100% causes sticky header cells of panels to not work. */
  height: `calc(100vh - ${props.topOffset}px - ${props.nav4Offset || 0}px)`,
  justifyContent: 'flex-end',
  position: 'sticky',
  top: `${props.topOffset}px`,
}));

type SidebarPlaceholderProps = {
  isBeingRenderedInsideMobileNav: boolean;
};
// SidebarPlaceholder is not used by nav4. It is only referenced by the mobile header
// which is being removed separately in COREX-13403
// The mobile sidebar is not always rendered, so we need to trigger a re-render
// of the Sidebar component when this component becomes rendered
export const SidebarPlaceholder = ({
  isBeingRenderedInsideMobileNav,
}: SidebarPlaceholderProps) => {
  const sidebarContext = useContext(SidebarContext);
  const { consentBannerHeight } = useConsentBanner()[0];

  useEffect(() => {
    sidebarContext.renderSidebar();
    // intentionally empty useEffect deps to simulate componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topOffset = useSelector((state: BucketState) =>
    getCombinedBannerAndHorizontalNavHeight(
      state,
      isBeingRenderedInsideMobileNav,
      consentBannerHeight
    )
  );

  return <Container id="bb-sidebar" topOffset={topOffset} />;
};

// same as SidebarPlaceholder without the topOffset calculations and mobile stuff
export const SidebarPlaceholderNavV4 = () => {
  const sidebarContext = useContext(SidebarContext);
  const { consentBannerHeight } = useConsentBanner()[0];
  const topOffset = useSelector((state: BucketState) =>
    getCombinedBannerAndHorizontalNavHeight(state, false, consentBannerHeight)
  );

  useEffect(() => {
    sidebarContext.renderSidebar();
    // intentionally empty useEffect deps to simulate componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      id="bb-sidebar"
      data-testid="bb-sidebar"
      topOffset={0}
      nav4Offset={topOffset}
    />
  );
};
